import React, { useEffect, useRef, useId } from "react";
import { createPortal } from "react-dom";
import { FiX } from "react-icons/fi";
import "./Modal.css";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Reusable dialog: portal, role="dialog" + aria-modal, Escape / backdrop / X to close,
 * focus trap, body scroll lock and focus restore.
 *
 * accent: "blue" (default) | "green" - sets the focus ring / primary colour of the dialog and its form fields.
 * footer: node rendered in the right-aligned footer (e.g. Cancel + submit buttons).
 */
const Modal = ({
  open,
  onClose,
  title,
  subtitle,
  icon,
  accent = "blue",
  size = "md",
  footer,
  children,
}) => {
  const dialogRef = useRef(null);
  const titleId = useId();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;

    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const dialog = dialogRef.current;
    // Focus the first form control, falling back to the dialog itself
    const first = dialog?.querySelector("input, select, textarea") || dialog?.querySelector(FOCUSABLE);
    (first || dialog)?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onCloseRef.current?.();
        return;
      }
      if (event.key !== "Tab" || !dialog) return;

      const items = Array.from(dialog.querySelectorAll(FOCUSABLE));
      if (items.length === 0) {
        event.preventDefault();
        return;
      }
      const firstItem = items[0];
      const lastItem = items[items.length - 1];
      if (event.shiftKey && document.activeElement === firstItem) {
        event.preventDefault();
        lastItem.focus();
      } else if (!event.shiftKey && document.activeElement === lastItem) {
        event.preventDefault();
        firstItem.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused && typeof previouslyFocused.focus === "function") previouslyFocused.focus();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="ahd-modal-overlay"
      onMouseDown={(event) => {
        // Close only when the press starts on the backdrop itself
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <div
        ref={dialogRef}
        className={`ahd-modal ahd-modal--${size} ahd-modal--${accent}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className="ahd-modal__header">
          <div className="ahd-modal__heading">
            <h2 id={titleId} className="ahd-modal__title">
              {icon && <span className="ahd-modal__icon">{icon}</span>}
              <span>{title}</span>
            </h2>
            {subtitle && <p className="ahd-modal__subtitle">{subtitle}</p>}
          </div>
          <button type="button" className="ahd-modal__close" onClick={onClose} aria-label="Close dialog">
            <FiX size={20} />
          </button>
        </div>
        <div className="ahd-modal__body">{children}</div>
        {footer && <div className="ahd-modal__footer">{footer}</div>}
      </div>
    </div>,
    document.body
  );
};

export default Modal;
